import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { useState } from 'react';

interface RemindClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientName: string;
  clientEmail: string;
  policyUuid: string;
  onSendReminder: (policyUuid: string) => Promise<void>;
}

const RemindClientModal = ({
  isOpen,
  onClose,
  clientName,
  clientEmail,
  policyUuid,
  onSendReminder,
}: RemindClientModalProps) => {
  const [isSending, setIsSending] = useState(false);
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'navy.700');

  const handleSendReminder = async () => {
    setIsSending(true);
    try {
      await onSendReminder(policyUuid);
      toast({
        title: 'Reminder sent!',
        description: `Email reminder sent to ${clientEmail}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      onClose();
    } catch (error) {
      console.error('Failed to send reminder:', error);
      toast({
        title: 'Failed to send reminder',
        description: 'Please try again or contact support',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent bg={bgColor}>
        <ModalHeader>Remind Client - Document Upload Link</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing="20px" align="stretch">
            {/* Client Name */}
            <VStack align="start" spacing="8px">
              <Text fontSize="sm" fontWeight="600" color="gray.500">
                Client Name
              </Text>
              <Text fontSize="md" fontWeight="500">
                {clientName}
              </Text>
            </VStack>

            {/* Client Email */}
            <VStack align="start" spacing="8px">
              <Text fontSize="sm" fontWeight="600" color="gray.500">
                Client Email
              </Text>
              <Text fontSize="md" fontWeight="500">
                {clientEmail || 'No email on file'}
              </Text>
            </VStack>

            {/* Reminder Message */}
            <VStack align="start" spacing="8px">
              <Text fontSize="sm" color="gray.600">
                An email will be sent to the client with a secure, time-bound
                link (expires in 15 days) to upload their missing documents.
              </Text>
            </VStack>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="outline" mr={3} onClick={onClose}>
            Close
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSendReminder}
            isLoading={isSending}
            loadingText="Sending..."
          >
            Remind
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RemindClientModal;
